import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { InterventionProgressStep } from '../../../intervention.model';

// The steps of the wizard as one strip of tabs, each with a numbered badge showing how far it has got (done, in progress, not started), and the
// work that comes after the wizard (Tasks, Sessions, ...) as a second row. The case page uses it to move between the steps; the New Request page shows it so the user sees
// where the request sits in the wizard.
@Component({
  selector: 'wizard-steps-nav',
  imports: [...SharedMaterialUIModule],
  templateUrl: './wizard-steps-nav.component.html',
  styleUrl: './wizard-steps-nav.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WizardStepsNavComponent {
  readonly steps = input.required<InterventionProgressStep[]>();
  // The work after the wizard; none on the New Request page.
  readonly sections = input<{ key: string; label: string }[]>([]);
  // The step or section that is open.
  readonly active = input('');
  // False where the steps only show where the request is (the New Request page).
  readonly interactive = input(true);
  // False where the progress of the case is not known (a portal that is not told it, such as a provider): the steps are then only names to go to,
  // with no icon or number that would say how far each has got.
  readonly showProgress = input(true);
  // The step the case works on next, marked so the user sees where to go.
  readonly nextStepKey = input<string | null>(null);
  // The steps that cannot be opened yet (they wait for eligibility to pass).
  readonly lockedSteps = input<string[]>([]);
  // The section where the next task is done, and its name.
  readonly taskSection = input<string | null>(null);
  readonly taskName = input<string | null>(null);
  readonly selected = output<string>();

  protected isLocked(step: InterventionProgressStep): boolean {
    return this.lockedSteps().includes(step.key);
  }

  // What the number badge of a step shows instead of its number: a lock when it has to wait, a tick when it is done. In progress and not
  // started keep the number and differ by colour.
  protected badgeIcon(step: InterventionProgressStep): string | null {
    if (this.isLocked(step)) {
      return 'lock';
    }

    return step.state === 'Complete' ? 'check' : null;
  }

  // What the tooltip says: where the step stands and, when it is not done, what is missing.
  protected tooltip(step: InterventionProgressStep): string {
    if (this.isLocked(step)) {
      return 'Locked: eligibility has to pass first';
    }

    if (step.state === 'Complete') {
      return 'Done';
    }

    const stand = step.state === 'InProgress' ? 'In progress' : 'Not started';
    return step.missing ? `${stand}: ${step.missing}` : stand;
  }
}
